import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE || '';

// 十二时辰定义
const SHI_CHEN = [
  { name: '子', range: '23:00-01:00', hour: 23, emoji: '🌙' },
  { name: '丑', range: '01:00-03:00', hour: 1, emoji: '🐂' },
  { name: '寅', range: '03:00-05:00', hour: 3, emoji: '🐅' },
  { name: '卯', range: '05:00-07:00', hour: 5, emoji: '🐇' },
  { name: '辰', range: '07:00-09:00', hour: 7, emoji: '🐉' },
  { name: '巳', range: '09:00-11:00', hour: 9, emoji: '🐍' },
  { name: '午', range: '11:00-13:00', hour: 11, emoji: '🐎' },
  { name: '未', range: '13:00-15:00', hour: 13, emoji: '🐐' },
  { name: '申', range: '15:00-17:00', hour: 15, emoji: '🐒' },
  { name: '酉', range: '17:00-19:00', hour: 17, emoji: '🐓' },
  { name: '戌', range: '19:00-21:00', hour: 19, emoji: '🐕' },
  { name: '亥', range: '21:00-23:00', hour: 21, emoji: '🐖' },
];

const cities = [
  '北京', '上海', '广州', '深圳', '成都', '杭州', '武汉', '西安',
  '重庆', '南京', '天津', '苏州', '郑州', '长沙', '沈阳', '青岛',
  '宁波', '东莞', '佛山', '合肥', '昆明', '大连', '厦门', '哈尔滨',
  '济南', '温州', '长春', '石家庄', '南宁', '常州', '泉州', '贵阳',
  '南昌', '金华', '乌鲁木齐', '呼和浩特', '兰州', '海口', '银川',
  '西宁', '拉萨', '台北', '香港', '澳门'
];

const lunarMonths = ['正月','二月','三月','四月','五月','六月','七月','八月','九月','十月','冬月','腊月'];
const lunarDays = ['初一','初二','初三','初四','初五','初六','初七','初八','初九','初十',
  '十一','十二','十三','十四','十五','十六','十七','十八','十九','二十',
  '廿一','廿二','廿三','廿四','廿五','廿六','廿七','廿八','廿九','三十'];

function InputPage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || null;
  const [loading, setLoading] = useState(false);
  const [calendarType, setCalendarType] = useState('lunar'); // 默认农历

  // 国历日期
  const [solarDate, setSolarDate] = useState({ year: 1990, month: 1, day: 1 });
  // 农历日期
  const [lunarDate, setLunarDate] = useState({ year: 1990, month: 1, day: 1, isLeapMonth: false });
  // 选中的时辰索引
  const [selectedShiChen, setSelectedShiChen] = useState(5); // 默认午时(11-13点)
  // 出生时间(从时辰推算)
  const [birthTime, setBirthTime] = useState({ hour: 11, minute: 0 });
  const [gender, setGender] = useState('male');
  const [location, setLocation] = useState('北京');
  const [fuzzyHour, setFuzzyHour] = useState(false); // 时辰未知

  // 国历→农历校验显示
  const [lunarHint, setLunarHint] = useState(null); // { lunar_str, loading }
  const [hintTimer, setHintTimer] = useState(null);

  // 快捷输入日期（手动输入文本）
  const [dateInputValue, setDateInputValue] = useState('');

  // 初始化默认日期
  useEffect(() => {
    const today = new Date();
    setSolarDate({ year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() });
    setLunarDate({ year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate(), isLeapMonth: false });
  }, []);

  // TimeFixPage 传入的 suggestedHour（寻时定盘结果）
  useEffect(() => {
    const suggested = routerLocation.state?.suggestedHour;
    if (suggested?.zhi) {
      const idx = SHI_CHEN.findIndex(sc => sc.name === suggested.zhi);
      if (idx !== -1) {
        setSelectedShiChen(idx);
        setBirthTime({ hour: SHI_CHEN[idx].hour, minute: 0 });
      }
    }
  }, [routerLocation.state]);

  // 国历变化时，获取对应农历用于校验
  const fetchLunarHint = useCallback(async (y, m, d) => {
    if (calendarType !== 'solar') return;
    setLunarHint(prev => ({ ...prev, loading: true }));
    try {
      const res = await axios.post(`${API_BASE}/api/solar-to-lunar`, { year: y, month: m, day: d });
      if (res.data.success) {
        setLunarHint({ lunar_str: res.data.data.lunar_str, loading: false });
      } else {
        setLunarHint({ lunar_str: null, loading: false });
      }
    } catch {
      setLunarHint({ lunar_str: null, loading: false });
    }
  }, [calendarType]);

  // 防抖调用
  useEffect(() => {
    if (calendarType !== 'solar') { setLunarHint(null); return; }
    if (hintTimer) clearTimeout(hintTimer);
    const timer = setTimeout(() => {
      fetchLunarHint(solarDate.year, solarDate.month, solarDate.day);
    }, 400);
    setHintTimer(timer);
    return () => clearTimeout(timer);
  }, [solarDate, calendarType]); // eslint-disable-line

  const handleSolarChange = (field, value) => {
    setSolarDate(prev => ({ ...prev, [field]: parseInt(value) || 1 }));
  };

  const handleLunarChange = (field, value) => {
    setLunarDate(prev => ({ ...prev, [field]: parseInt(value) || 1 }));
  };

  const handleShiChenSelect = (index) => {
    setSelectedShiChen(index);
    setBirthTime({ hour: SHI_CHEN[index].hour, minute: 0 });
  };

  // 处理快捷日期输入：支持 YYYY-MM-DD / YYYY/MM/DD / YYYY.MM.DD / YYYY年MM月DD日
  // 以及 YYYYMMDD 纯数字格式
  const handleDateInput = (raw) => {
    setDateInputValue(raw);
    if (!raw.trim()) return;

    let match;
    // 匹配 YYYY-MM-DD / YYYY/MM/DD / YYYY.MM.DD
    match = raw.match(/^(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})$/);
    if (!match) {
      // 匹配 YYYY年MM月DD日
      match = raw.match(/^(\d{4})年\s*(\d{1,2})月\s*(\d{1,2})日?$/);
    }
    if (!match) {
      // 匹配 YYYYMMDD 纯数字
      match = raw.match(/^(\d{4})(\d{2})(\d{2})$/);
    }
    if (match) {
      let y = parseInt(match[1]);
      let m = parseInt(match[2]);
      let d = parseInt(match[3]);
      if (y < 1900 || y > 2100) return;
      if (m < 1 || m > 12) return;
      if (d < 1 || d > 31) return;

      if (calendarType === 'solar') {
        setSolarDate({ year: y, month: m, day: d });
      } else {
        setLunarDate(prev => ({ ...prev, year: y, month: m, day: d }));
      }
    }
  };

  // 检测时辰边界
  const getShiChenBoundaryWarning = () => {
    const sc = SHI_CHEN[selectedShiChen];
    const hour = sc.hour;
    // 每个时辰2小时，边界在整点前后15分钟
    const nearStart = birthTime.hour === hour && birthTime.minute <= 15;
    const nearEndHour = (hour + 2) % 24;
    const nearEnd = (birthTime.hour === nearEndHour - 1 && birthTime.minute >= 45)
      || (birthTime.hour === (nearEndHour === 0 ? 23 : nearEndHour - 1) && birthTime.minute >= 45);
    if (nearStart || nearEnd) {
      return `⚠️ 你的出生时间接近「${sc.name}时」的时分边界，若实际时辰有偏差，排盘结果可能不同`;
    }
    return null;
  };

  const boundaryWarning = getShiChenBoundaryWarning();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 用户通过十二时辰按钮选择出生时间时，时辰已经是传统时间体系，
      // 不应叠加真太阳时修正（否则边界值如子时23:00会被修正到亥时22:xx）
      const isShiChenSelection = birthTime.minute === 0 && SHI_CHEN.some(sc => sc.hour === birthTime.hour);
      
      const formData = {
        year: calendarType === 'solar' ? solarDate.year : lunarDate.year,
        month: calendarType === 'solar' ? solarDate.month : lunarDate.month,
        day: calendarType === 'solar' ? solarDate.day : lunarDate.day,
        hour: fuzzyHour ? 11 : birthTime.hour,
        minute: fuzzyHour ? 0 : birthTime.minute,
        gender: gender,
        location: location,
        calendar_type: calendarType,
        is_leap_month: calendarType === 'lunar' ? lunarDate.isLeapMonth : false,
        fuzzy_hour: fuzzyHour,
        use_true_solar_time: isShiChenSelection ? false : true
      };

      const response = await axios.post(`${API_BASE}/api/bazi`, formData, {
        timeout: 15000, // 15秒超时，避免无限等待
      });
      if (response.data.success) {
        navigate('/result', { state: { baziData: response.data.data, returnTo } });
      } else {
        alert('排盘失败: ' + (response.data.message || '请稍后重试'));
      }
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        alert('请求超时 (15秒)，后端服务未响应。请在终端执行:\n\ncd backend && python3 main.py\n\n重新启动后端后再试。');
      } else if (!error.response) {
        alert('无法连接后端服务。请确认已启动:\n\ncd backend && python3 main.py');
      } else {
        alert('请求失败: ' + (error.response?.data?.message || error.message));
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md mx-auto"
      >
        {/* 返回按钮 */}
        <button
          onClick={() => navigate('/')}
          className="mb-6 text-stone hover:text-ink transition-colors"
        >
          ← 返回首页
        </button>

        <h2 className="text-2xl font-bold font-serif text-ink mb-8 text-center">
          请输入您的出生信息
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ===== 历法选择 ===== */}
          <div>
            <label className="block text-sm font-medium text-ink mb-3">
              出生日期类型
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setCalendarType('lunar')}
                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                  calendarType === 'lunar'
                    ? 'bg-zhusha text-white shadow-md'
                    : 'bg-white text-stone border border-stone/30 hover:border-zhusha'
                }`}
              >
                <div className="text-lg">农历</div>
                <div className="text-xs opacity-80">阴历 / 旧历</div>
              </button>
              <button
                type="button"
                onClick={() => setCalendarType('solar')}
                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                  calendarType === 'solar'
                    ? 'bg-zhusha text-white shadow-md'
                    : 'bg-white text-stone border border-stone/30 hover:border-zhusha'
                }`}
              >
                <div className="text-lg">国历</div>
                <div className="text-xs opacity-80">公历 / 阳历</div>
              </button>
            </div>
          </div>

          {/* ===== 日期选择 ===== */}
          <AnimatePresence mode="wait">
            {calendarType === 'solar' ? (
              <motion.div
                key="solar"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <label className="block text-sm font-medium text-ink mb-2">
                  出生日期（国历）
                </label>
                {/* 快捷输入：手动输入日期 */}
                <div className="mb-2 relative">
                  <input
                    type="text"
                    value={dateInputValue}
                    onChange={(e) => handleDateInput(e.target.value)}
                    placeholder="快捷输入，如 1990-5-15 或 19900515"
                    className="w-full px-3 py-2.5 pr-10 border border-stone/30 rounded-xl focus:outline-none focus:border-zhusha bg-white text-sm text-ink placeholder:text-stone/40"
                  />
                  {solarDate.year && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone/40">
                      {solarDate.year}-{solarDate.month}-{solarDate.day}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <select
                      value={solarDate.year}
                      onChange={(e) => handleSolarChange('year', e.target.value)}
                      className="w-full px-3 py-3 border border-stone/30 rounded-xl focus:outline-none focus:border-zhusha bg-white text-center"
                    >
                      {Array.from({ length: 100 }, (_, i) => 1920 + i).map(year => (
                        <option key={year} value={year}>{year}年</option>
                      ))}
                    </select>
                  </div>
                  <select
                    value={solarDate.month}
                    onChange={(e) => handleSolarChange('month', e.target.value)}
                    className="w-20 px-2 py-3 border border-stone/30 rounded-xl focus:outline-none focus:border-zhusha bg-white text-center"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <option key={m} value={m}>{m}月</option>
                    ))}
                  </select>
                  <select
                    value={solarDate.day}
                    onChange={(e) => handleSolarChange('day', e.target.value)}
                    className="w-20 px-2 py-3 border border-stone/30 rounded-xl focus:outline-none focus:border-zhusha bg-white text-center"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <option key={d} value={d}>{d}日</option>
                    ))}
                  </select>
                </div>

                {/* 农历校验提示 */}
                <AnimatePresence>
                  {lunarHint && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200"
                    >
                      {lunarHint.loading ? (
                        <p className="text-sm text-amber-700 flex items-center gap-2">
                          <span className="inline-block w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></span>
                          正在计算对应农历...
                        </p>
                      ) : lunarHint.lunar_str ? (
                        <div>
                          <p className="text-xs text-amber-600 mb-1">对应农历日期（请核实）：</p>
                          <p className="text-base font-bold text-amber-800 font-serif">
                            📅 {lunarHint.lunar_str}
                          </p>
                        </div>
                      ) : (
                        <p className="text-sm text-red-500">无法计算对应农历，请检查日期</p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ) : (
              <motion.div
                key="lunar"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <label className="block text-sm font-medium text-ink mb-2">
                  出生日期（农历）
                </label>
                {/* 快捷输入：手动输入日期 */}
                <div className="mb-2 relative">
                  <input
                    type="text"
                    value={dateInputValue}
                    onChange={(e) => handleDateInput(e.target.value)}
                    placeholder="快捷输入，如 1990-5-15 或 19900515"
                    className="w-full px-3 py-2.5 pr-10 border border-stone/30 rounded-xl focus:outline-none focus:border-zhusha bg-white text-sm text-ink placeholder:text-stone/40"
                  />
                  {lunarDate.year && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone/40">
                      {lunarDate.year}-{lunarDate.month}-{lunarDate.day}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <select
                    value={lunarDate.year}
                    onChange={(e) => handleLunarChange('year', e.target.value)}
                    className="flex-1 px-3 py-3 border border-stone/30 rounded-xl focus:outline-none focus:border-zhusha bg-white text-center"
                  >
                    {Array.from({ length: 100 }, (_, i) => 1920 + i).map(year => (
                      <option key={year} value={year}>{year}年</option>
                    ))}
                  </select>
                  <select
                    value={lunarDate.month}
                    onChange={(e) => handleLunarChange('month', e.target.value)}
                    className="flex-1 px-2 py-3 border border-stone/30 rounded-xl focus:outline-none focus:border-zhusha bg-white text-center"
                  >
                    {lunarMonths.map((m, i) => (
                      <option key={i+1} value={i+1}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={lunarDate.day}
                    onChange={(e) => handleLunarChange('day', e.target.value)}
                    className="flex-1 px-2 py-3 border border-stone/30 rounded-xl focus:outline-none focus:border-zhusha bg-white text-center"
                  >
                    {lunarDays.map((d, i) => (
                      <option key={i+1} value={i+1}>{d}</option>
                    ))}
                  </select>
                </div>
                {/* 闰月开关 */}
                <div className="mt-2 flex items-center gap-2 p-2 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={lunarDate.isLeapMonth}
                      onChange={(e) => setLunarDate(prev => ({ ...prev, isLeapMonth: e.target.checked }))}
                      className="w-4 h-4 text-zhusha rounded focus:ring-zhusha"
                    />
                    <span className="text-sm text-stone">闰月出生</span>
                  </label>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ===== 出生时辰（十二时辰网格） ===== */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-ink">
                出生时辰
              </label>
              {/* 时辰未知开关 */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <span className={`text-xs transition-colors ${fuzzyHour ? 'text-zhusha font-medium' : 'text-stone/60'}`}>
                  时辰未知
                </span>
                <button
                  type="button"
                  onClick={() => setFuzzyHour(!fuzzyHour)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    fuzzyHour ? 'bg-zhusha' : 'bg-stone/30'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                      fuzzyHour ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </label>
            </div>

            {/* 时辰网格：未知时隐藏 */}
            {!fuzzyHour && (
              <>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 sm:gap-2">
                  {SHI_CHEN.map((sc, index) => (
                    <button
                      key={sc.name}
                      type="button"
                      onClick={() => handleShiChenSelect(index)}
                      className={`flex flex-col items-center justify-center py-2.5 sm:py-3 px-0.5 sm:px-1 rounded-xl border-2 transition-all ${
                        selectedShiChen === index
                          ? 'border-zhusha bg-zhusha/10 shadow-sm'
                          : 'border-stone/20 bg-white hover:border-stone/40'
                      }`}
                    >
                      <span className="text-[10px] sm:text-xs opacity-60">{sc.emoji}</span>
                      <span className={`text-base sm:text-lg font-bold font-serif ${
                        selectedShiChen === index ? 'text-zhusha' : 'text-ink'
                      }`}>
                        {sc.name}
                      </span>
                      <span className="text-[9px] sm:text-[10px] text-stone/70 leading-tight text-center">
                        {sc.range}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-stone/60 mt-2 text-center">
                  选中时辰：<span className="font-bold text-zhusha">{SHI_CHEN[selectedShiChen].name}时</span>
                  （{SHI_CHEN[selectedShiChen].range}）
                </p>

                {/* 时辰边界检测 */}
                {boundaryWarning && (
                  <div className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                    <p className="text-xs text-amber-700 leading-relaxed">{boundaryWarning}</p>
                  </div>
                )}
              </>
            )}

            {/* 时辰未知说明 */}
            {fuzzyHour && (
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                <div className="flex items-start gap-2">
                  <span className="text-blue-500 text-lg">⏳</span>
                  <div>
                    <p className="text-sm font-medium text-blue-800 mb-2">
                      时辰未知模式
                    </p>
                    <p className="text-xs text-blue-600 leading-relaxed mb-3">
                      将以<span className="font-bold">午时（11:00）</span>作为默认基准进行推算，时柱结果仅供参考。
                      年、月、日三柱不受影响。
                    </p>
                    <ul className="text-xs text-blue-500 space-y-1 list-disc list-inside">
                      <li>日主、格局、大运起运计算不受影响</li>
                      <li>时柱十神、神煞、五行统计可能不准确</li>
                      <li>建议通过「寻时定盘」功能核实时辰</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* 寻时定盘入口 */}
            {!fuzzyHour && (
              <div className="mt-2 text-center">
                <button
                  type="button"
                  onClick={() => navigate('/time-fix')}
                  className="text-xs text-blue-500 hover:text-blue-700 transition-colors inline-flex items-center gap-1"
                >
                  <span>🔍</span> 时辰不确定？用寻时定盘来验证
                </button>
              </div>
            )}
          </div>

          {/* ===== 性别 + 地点 ===== */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* 性别 */}
            <div>
              <label className="block text-sm font-medium text-ink mb-2">
                性别
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setGender('male')}
                  className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                    gender === 'male'
                      ? 'bg-zhusha text-white shadow-sm'
                      : 'bg-white text-stone border border-stone/30 hover:border-zhusha'
                  }`}
                >
                  男
                </button>
                <button
                  type="button"
                  onClick={() => setGender('female')}
                  className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                    gender === 'female'
                      ? 'bg-zhusha text-white shadow-sm'
                      : 'bg-white text-stone border border-stone/30 hover:border-zhusha'
                  }`}
                >
                  女
                </button>
              </div>
            </div>

            {/* 出生地点 */}
            <div>
              <label className="block text-sm font-medium text-ink mb-2">
                出生地点
              </label>
              <input
                type="text"
                list="city-list"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="输入城市名或从列表选择"
                className="w-full px-3 py-3 border border-stone/30 rounded-xl focus:outline-none focus:border-zhusha bg-white"
              />
              <datalist id="city-list">
                {cities.map(city => (
                  <option key={city} value={city} />
                ))}
              </datalist>
            </div>
          </div>

          {/* ===== 隐私说明 ===== */}
          <div className="flex items-center justify-center gap-1.5 text-xs text-stone/60">
            <span>🔒</span>
            <span>八字排盘在本地完成，记录仅保存在您的设备</span>
          </div>

          {/* ===== 提交按钮 ===== */}
          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-4 bg-zhusha text-white text-lg font-medium rounded-full shadow-lg hover:shadow-xl transition-shadow disabled:opacity-50"
          >
            {loading ? '排盘中...' : '开始排盘'}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
}

export default InputPage;
